import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Save, Loader2, Trash2, HardHat, Mail, Phone, Smartphone, MapPin, Briefcase } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';
import { PhotoGallery } from '@/components/PhotoGallery';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';

export default function EmployeeDetailPage() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const tf = (fr, en) => language === 'fr' ? fr : en;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '', email: '', position: 'poseur', status: 'actif', notes: '' });
  const [createdAt, setCreatedAt] = useState('');
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [mobilePassword, setMobilePassword] = useState('');
  const [settingPassword, setSettingPassword] = useState(false);
  const [assignedClients, setAssignedClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);

  const handleSetPassword = async () => {
    if (mobilePassword.length < 6) {
      toast.error(tf('Le mot de passe doit avoir au moins 6 caracteres', 'Password must be at least 6 characters'));
      return;
    }
    setSettingPassword(true);
    try {
      await api.post(`/mobile/employees/${employeeId}/set-password`, { password: mobilePassword });
      toast.success(tf('Acces mobile active!', 'Mobile access activated!'));
      setShowPasswordDialog(false);
      setMobilePassword('');
    } catch (e) {
      toast.error(e.response?.data?.detail || tf('Erreur', 'Error'));
    } finally { setSettingPassword(false); }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get(`/employees/${employeeId}`);
        setForm({
          first_name: data.first_name || '', last_name: data.last_name || '',
          phone: data.phone || '', email: data.email || '',
          position: data.position || 'autre', status: data.status || 'actif',
          notes: data.notes || '',
        });
        setCreatedAt(data.created_at || '');
      } catch {
        toast.error(tf('Employe introuvable', 'Employee not found'));
        navigate('/employees');
      } finally { setLoading(false); }
    };
    load();
  }, [employeeId]);

  useEffect(() => {
    const loadClients = async () => {
      try {
        const { data } = await api.get('/clients?search=');
        const clients = data.clients || [];
        const mine = clients.filter(c =>
          (c.assigned_representative_ids || []).includes(employeeId) ||
          c.assigned_representative_id === employeeId
        );
        setAssignedClients(mine);
      } catch (e) { console.error('loadAssignedClients failed:', e); }
      finally { setLoadingClients(false); }
    };
    loadClients();
  }, [employeeId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/employees/${employeeId}`, form);
      toast.success(tf('Employe mis a jour', 'Employee updated'));
    } catch (e) { toast.error(e.response?.data?.detail || tf('Erreur', 'Error')); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(tf('Supprimer cet employe?', 'Delete this employee?'))) return;
    try {
      await api.delete(`/employees/${employeeId}`);
      toast.success(tf('Employe supprime', 'Employee deleted'));
      navigate('/employees');
    } catch { toast.error(tf('Erreur', 'Error')); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="employee-detail-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/employees')} data-testid="back-to-employees">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-black tracking-tight">
              {form.first_name} {form.last_name}
            </h1>
            {createdAt && (
              <p className="text-sm text-muted-foreground">
                {tf('Ajoute le', 'Added')} {new Date(createdAt).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA')}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowPasswordDialog(true)} data-testid="set-mobile-password-btn">
            <Smartphone className="h-4 w-4 mr-2" />{tf('Acces mobile', 'Mobile access')}
          </Button>
          <Button variant="destructive" size="sm" onClick={handleDelete} data-testid="delete-employee-btn">
            <Trash2 className="h-4 w-4 mr-2" />{tf('Supprimer', 'Delete')}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <HardHat className="h-5 w-5" />
            {tf('Informations de l\'employe', 'Employee information')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{tf('Prenom', 'First name')}</Label>
              <Input value={form.first_name} onChange={e => setForm(f => ({...f, first_name: e.target.value}))} data-testid="edit-emp-firstname" />
            </div>
            <div className="space-y-1.5">
              <Label>{tf('Nom', 'Last name')}</Label>
              <Input value={form.last_name} onChange={e => setForm(f => ({...f, last_name: e.target.value}))} data-testid="edit-emp-lastname" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {tf('Telephone', 'Phone')}</Label>
              <Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} data-testid="edit-emp-phone" />
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Courriel</Label>
              <Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} data-testid="edit-emp-email" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{tf('Poste', 'Position')}</Label>
              <Select value={form.position} onValueChange={v => setForm(f => ({...f, position: v}))}>
                <SelectTrigger data-testid="edit-emp-position"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="poseur">Poseur</SelectItem>
                  <SelectItem value="operateur">{tf('Operateur', 'Operator')}</SelectItem>
                  <SelectItem value="contremaitre">{tf('Contremaitre', 'Foreman')}</SelectItem>
                  <SelectItem value="autre">{tf('Autre', 'Other')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{tf('Statut', 'Status')}</Label>
              <Select value={form.status} onValueChange={v => setForm(f => ({...f, status: v}))}>
                <SelectTrigger data-testid="edit-emp-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="actif">{tf('Actif', 'Active')}</SelectItem>
                  <SelectItem value="inactif">{tf('Inactif', 'Inactive')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} rows={4} data-testid="edit-emp-notes" />
          </div>
          <Button onClick={handleSave} disabled={saving} data-testid="save-employee-edit-btn">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            {tf('Sauvegarder', 'Save')}
          </Button>
        </CardContent>
      </Card>

      <PhotoGallery entityType="employee" entityId={employeeId} />

      {/* Chantiers assignes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Briefcase className="h-5 w-5" />
            {tf('Chantiers assignes', 'Assigned worksites')} ({assignedClients.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingClients ? (
            <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : assignedClients.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4" data-testid="no-assigned-clients">
              {tf('Aucun chantier assigne', 'No assigned worksites')}
            </p>
          ) : (
            <div className="space-y-2" data-testid="assigned-clients-list">
              {assignedClients.map(c => {
                const STATUS_MAP = {
                  signe: { label: tf('Signe', 'Signed'), cls: 'bg-blue-500' },
                  travaux_en_cours: { label: tf('Travaux en cours', 'In progress'), cls: 'bg-orange-500' },
                  complete: { label: tf('Complete', 'Completed'), cls: 'bg-green-500' },
                  annule: { label: tf('Annule', 'Cancelled'), cls: 'bg-red-500' },
                };
                const st = STATUS_MAP[c.status] || STATUS_MAP.signe;
                return (
                  <div
                    key={c.client_id}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/30 cursor-pointer transition-colors"
                    onClick={() => navigate(`/clients/${c.client_id}`)}
                    data-testid={`assigned-client-${c.client_id}`}
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-sm">{c.first_name} {c.last_name}</p>
                      {(c.address || c.city) && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                          <MapPin className="h-3 w-3 shrink-0" />
                          {[c.address, c.city].filter(Boolean).join(', ')}
                        </p>
                      )}
                    </div>
                    <Badge className={`${st.cls} text-white text-xs shrink-0`}>{st.label}</Badge>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Smartphone className="h-5 w-5" /> {tf('Acces mobile', 'Mobile access')}</DialogTitle>
            <DialogDescription>
              {tf(
                `Definissez un mot de passe pour que ${form.first_name} puisse se connecter a l'application mobile avec son courriel.`,
                `Set a password so ${form.first_name} can log into the mobile app with their email.`
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">{tf('Courriel', 'Email')}</label>
              <Input value={form.email} disabled className="bg-muted" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">{tf('Mot de passe mobile', 'Mobile password')}</label>
              <Input
                type="password" value={mobilePassword}
                onChange={e => setMobilePassword(e.target.value)}
                placeholder={tf('Minimum 6 caracteres', 'Minimum 6 characters')}
                data-testid="mobile-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>{tf('Annuler', 'Cancel')}</Button>
            <Button onClick={handleSetPassword} disabled={settingPassword} data-testid="confirm-set-password-btn">
              {settingPassword && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {tf('Activer', 'Activate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
